import { infiniteQueryOptions, useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import { conversationKeys } from "./conversation-keys";
import { toScrimOptions, toLfgCards, toChatLists, toChatMessage, toChatMessagesPage, toChatPins, toConversation, toConversationMember, toMediaCounts, toMediaItems } from "../lib/to-conversation";
import type { ChatMediaItem, ChatMediaKind, ChatMessagesPage } from "../model/types";

const PAGE_SIZE = 50;

/** KAN-1483: эмодзи реакций задаёт сервер — его же он и примет. Список не меняется в рантайме. */
export function useReactionEmojis() {
  return useQuery({
    queryKey: [...conversationKeys.all, "reactions"],
    queryFn: async () => {
      const { data } = await api.get<unknown>("/conversations/reactions");
      return Array.isArray(data) ? data.filter((x): x is string => typeof x === "string") : [];
    },
    staleTime: Infinity,
  });
}

export function useConversations(archived: boolean, enabled = true) {
  return useQuery({
    queryKey: conversationKeys.list(archived),
    queryFn: async () => {
      const { data } = await api.get<unknown[] | null>("/conversations", { params: { archived } });
      return (data ?? []).map(toConversation);
    },
    enabled,
    // Ник и аватар собеседника живут в профиле: глобально refetch отключён, поэтому после смены ника в чате оставался старый.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

/** KAN-1333: личные списки чатов. Пока чат не открыт — не грузятся. */
export function useChatLists(enabled = true) {
  return useQuery({
    queryKey: conversationKeys.chatLists(),
    queryFn: async () => {
      const { data } = await api.get<unknown>("/conversations/lists");
      return toChatLists(data);
    },
    enabled,
  });
}

/** KAN-1337: какие команды можно выбрать для скрима в этой личке. Грузится, когда открыта модалка. */
export function useScrimOptions(conversationId: string, enabled: boolean) {
  return useQuery({
    queryKey: conversationKeys.scrimOptions(conversationId),
    queryFn: async () => toScrimOptions((await api.get<unknown>(`/conversations/${encodeURIComponent(conversationId)}/cards/scrim/options`)).data),
    enabled,
    gcTime: 0,
  });
}

/** KAN-1336: «Мои объявления» — что можно прикрепить к сообщению. Грузится, когда открыта модалка. */
export function useMyLfgCards(enabled: boolean) {
  return useQuery({
    queryKey: conversationKeys.myLfgCards(),
    queryFn: async () => toLfgCards((await api.get<unknown>("/conversations/cards/lfg/mine")).data),
    enabled,
  });
}

export function useConversation(conversationId: string | null) {
  return useQuery({
    queryKey: conversationKeys.detail(conversationId ?? ""),
    queryFn: async () => {
      const { data } = await api.get<unknown>(`/conversations/${encodeURIComponent(conversationId!)}`);
      return toConversation(data);
    },
    enabled: !!conversationId,
  });
}

type MessagesCursor = { before: string; beforeId: string };

/** Страницы идут от новых к старым; экран склеивает их в хронологию через flattenPages. */
export function useConversationMessages(conversationId: string | null) {
  const qc = useQueryClient();
  const key = conversationKeys.messages(conversationId ?? "");
  return useInfiniteQuery({
    queryKey: key,
    queryFn: async ({ pageParam, signal }): Promise<ChatMessagesPage> => {
      const { data } = await api.get<unknown>(`/conversations/${encodeURIComponent(conversationId!)}/messages`, {
        params: { limit: PAGE_SIZE, ...pageParam }, signal,
      });
      const page = toChatMessagesPage(data);
      if (pageParam) return page;
      // Перечитали свежую страницу (возврат во вкладку), а свои сообщения ещё отправляются — оставляем их в конце.
      // Иначе они на миг пропадали из ленты и возвращались, когда приходил ответ сервера.
      // Только те, чья отправка правда ещё идёт: копия, вернувшаяся откатом чужой мутации, не должна висеть вечно.
      const cached = qc.getQueryData<InfiniteData<ChatMessagesPage>>(key);
      const ids = new Set(page.messages.map((m) => m.id));
      const sending = new Set(
        qc.getMutationCache().findAll({ status: "pending" }).map((mu) => (mu.state.variables as { tempId?: string } | undefined)?.tempId),
      );
      const pending = (cached?.pages[0]?.messages ?? []).filter((m) => m.isPending && !ids.has(m.id) && (sending.has(m.id) || m.isFailed));
      return pending.length > 0 ? { ...page, messages: [...page.messages, ...pending] } : page;
    },
    initialPageParam: undefined as MessagesCursor | undefined,
    // Курсор (время, id): сообщения с одинаковым временем на границе страниц не теряются.
    getNextPageParam: (last): MessagesCursor | undefined =>
      last.hasMore && last.messages[0] ? { before: last.messages[0].sentAtUtc, beforeId: last.messages[0].id } : undefined,
    enabled: !!conversationId,
    // Имена и аватары отправителей приходят с сообщениями: вернулись во вкладку — подтягиваем свежие (глобально refetch выключен).
    staleTime: 30_000,
    // Пролистали далеко назад — возврат во вкладку не перечитывает десятки страниц подряд.
    refetchOnWindowFocus: (q) => (q.state.data?.pages.length ?? 0) <= 2,
  });
}

/** KAN-1332: поиск по беседе; range — необязательный фильтр по датам (только выдача, история не трогается). */
export function useConversationSearch(conversationId: string | null, query: string, range?: { from: string; to: string } | null) {
  const q = query.trim();
  return useQuery({
    queryKey: conversationKeys.search(conversationId ?? "", q, range?.from, range?.to),
    queryFn: async ({ signal }) => {
      const { data } = await api.get<unknown[] | null>(`/conversations/${encodeURIComponent(conversationId!)}/search`, {
        params: { q, limit: 100, ...(range ? { from: range.from, to: range.to } : {}) }, signal,
      });
      return (data ?? []).map(toChatMessage);
    },
    enabled: !!conversationId && q.length >= 2,
    // Каждая буква запроса — свой ключ с сотней сообщений; держим недолго.
    gcTime: 30_000,
  });
}

export function useConversationMembers(conversationId: string | null, enabled = true) {
  return useQuery({
    queryKey: conversationKeys.members(conversationId ?? ""),
    queryFn: async () => {
      const { data } = await api.get<unknown[] | null>(`/conversations/${encodeURIComponent(conversationId!)}/members`);
      return (data ?? []).map(toConversationMember);
    },
    enabled: !!conversationId && enabled,
  });
}

/** KAN-1330: закрепы беседы в порядке закрепления. Список у каждого участника свой — обновляется событием PinsChanged. */
export function useConversationPins(conversationId: string | null) {
  return useQuery({
    queryKey: conversationKeys.pins(conversationId ?? ""),
    queryFn: async () => {
      const { data } = await api.get<unknown>(`/conversations/${encodeURIComponent(conversationId!)}/pins`);
      return toChatPins(data);
    },
    enabled: !!conversationId,
  });
}

/** KAN-1339: счётчики вкладок инфо-панели. */
export function useConversationMediaCounts(conversationId: string | null) {
  return useQuery({
    queryKey: conversationKeys.mediaCounts(conversationId ?? ""),
    queryFn: async () => {
      const { data } = await api.get<unknown>(`/conversations/${encodeURIComponent(conversationId!)}/media-counts`);
      return toMediaCounts(data);
    },
    enabled: !!conversationId,
  });
}

const MEDIA_PAGE = 60;

/** KAN-1339: запрос вкладки инфо-панели — общий для хука и для догрузки по требованию (просмотрщик фото+видео). */
export function conversationMediaQuery(conversationId: string, kind: ChatMediaKind) {
  return infiniteQueryOptions({
    queryKey: conversationKeys.media(conversationId, kind),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }): Promise<ChatMediaItem[]> => {
      const { data } = await api.get<unknown>(`/conversations/${encodeURIComponent(conversationId)}/media`, {
        params: { kind, limit: MEDIA_PAGE, ...(pageParam ? { before: pageParam } : {}) },
      });
      return toMediaItems(data);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.length >= MEDIA_PAGE ? last[last.length - 1]!.sentAtUtc : undefined),
  });
}

/** KAN-1339: одна вкладка инфо-панели, страницы вглубь по времени сообщения. Пока вкладка закрыта — не грузится. */
export function useConversationMedia(conversationId: string | null, kind: ChatMediaKind) {
  return useInfiniteQuery({ ...conversationMediaQuery(conversationId ?? "", kind), enabled: !!conversationId });
}
